import { useMemo, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { ChevronRight } from 'lucide-react';
import { Card, CardHeader, StatTile } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import { EmptyState, ErrorState, LoadingState } from '../../../components/ui/States';
import { DataNotes } from '../../../components/ui/InfoTip';
import { Select } from '../../../components/ui/Input';
import { Pagination, usePagination } from '../../../components/ui/Pagination';
import { Nil, Table, THead, Th, Td, Tr, isConstant } from '../../../components/ui/Table';
import { getRep } from '../../ProjectManagement/team';
import type { SalesRepSlug } from '../../ProjectManagement/team';
import { plural } from '../../ProjectManagement/formatters';
import { formatDayMonthTime, formatDayMonthYear } from '../../../utils/dateFormat';
import { SectionToolbar } from '../SectionToolbar';
import { AttioLink, RepInitials, companyLabel, formatShortDate, formatShortDateTime } from '../shared';
import type { MeetingsPeriod } from '../meetings/period';
import {
  useDemoBookings,
  type DemoBooking,
  type DemoBookingSource,
  type DemoBookingsResponse,
  type MeetingKind,
  type SdrMeetingsBridge,
} from './api';
import { bookingCompany } from './webmail';

const ATTIO_COMPANY_URL = (recordId: string) => `https://app.attio.com/sequesto/companies/record/${recordId}/overview`;

const personName = (p: { name: string | null; email?: string | null }) => p.name || p.email || 'unknown';

/**
 * "17 SDR meetings = 15 with a Calendly demo tagged SDR personal form + 1 booked
 * under another source + 1 not on Calendly", and the Calendly tag's own bridge.
 */
function SdrBridge({ bridge }: { bridge: SdrMeetingsBridge }) {
  const parts = [`${bridge.matched} with a Calendly demo tagged “SDR personal form” or “SDR (confirmed, no form)”`];
  if (bridge.onOtherSource.length > 0) {
    parts.push(
      `${bridge.onOtherSource.length} booked on Calendly under another source (${bridge.onOtherSource
        .map((m) => `${personName(m)}: ${m.sourceLabel}, ${m.bookingEmail}`)
        .join('; ')})`,
    );
  }
  if (bridge.withoutBooking.length > 0) {
    parts.push(
      `${bridge.withoutBooking.length} with no Calendly demo found (${bridge.withoutBooking
        .map((m) => `${personName(m)}, ${formatDayMonthYear(m.day)}`)
        .join('; ')})`,
    );
  }
  const calendly =
    bridge.calendlyCount === bridge.calendlyMatched
      ? null
      : `The two SDR sources below count ${bridge.calendlyCount}: ${bridge.calendlyMatched} of these + ${bridge.calendlyOnly.length} whose form falls outside this window or was not counted (${bridge.calendlyOnly
          .map((b) => personName(b))
          .join(', ')}).`;
  return (
    <p className="text-body-xs text-text-tertiary">
      <span className="font-medium text-text-secondary">SDR meetings:</span> {bridge.definition}{' '}
      {plural(bridge.count, 'SDR meeting')} in this window = {parts.join(' + ')}.{calendly ? ` ${calendly}` : ''}
    </p>
  );
}

/** Sources that answer "did marketing or the SDRs produce this?" first. */
const SOURCE_ORDER: DemoBookingSource[] = [
  'website_demo_request',
  'marketing_influenced',
  'sdr_personal_form',
  'sdr_confirmed',
  'round_robin_embed',
  'rep_personal_link',
  'patrick_calendly',
  'shared_link',
  'marketing_link',
  'partner',
  'unclassified',
];

/** Sources are categories, so they stay neutral; only "Unclassified" needs attention. */
const SOURCE_VARIANT: Record<DemoBookingSource, 'default' | 'warning'> = {
  website_demo_request: 'default',
  sdr_personal_form: 'default',
  sdr_confirmed: 'default',
  rep_personal_link: 'default',
  patrick_calendly: 'default',
  round_robin_embed: 'default',
  shared_link: 'default',
  marketing_link: 'default',
  marketing_influenced: 'default',
  partner: 'default',
  unclassified: 'warning',
};

/**
 * Source names as shown, by source id, so they read the same before and after
 * the API's labels change ("Rep personal link" and "Patrick's Calendly link"
 * looked like one thing). Falls back to the API's label for an unknown id.
 */
const SOURCE_NAMES: Partial<Record<DemoBookingSource, string>> = {
  rep_personal_link: "Nadia's / Andrew's own page",
  patrick_calendly: "Patrick's own page",
};
/** Whose page it is, for the hover on the short label. */
const SOURCE_HINTS: Partial<Record<DemoBookingSource, string>> = {
  rep_personal_link: "Nadia's or Andrew's personal Calendly page",
  patrick_calendly: 'calendly.com/sequesto',
};
/** One booking names the rep whose page it was ("Nadia's own page"); the filter lists both. */
const sourceName = (source: DemoBookingSource, apiLabel: string, hostRepSlug?: SalesRepSlug | null) =>
  source === 'rep_personal_link' && hostRepSlug
    ? `${getRep(hostRepSlug).name.split(' ')[0]}'s own page`
    : (SOURCE_NAMES[source] ?? apiLabel);

const KIND_LABELS: Record<MeetingKind, string> = {
  demo: 'Demo',
  discovery: 'Discovery',
  pricing: 'Pricing',
  trial: 'Trial',
  partner: 'Partner / co-sell',
  other: 'Other (30/45/60 min, onboarding, one-off)',
};

const FAR_FUTURE_DAYS = 60;

/** The meeting is more than FAR_FUTURE_DAYS after it was booked: likely a placeholder or a typo in the date. */
function isFarFuture(b: DemoBooking): boolean {
  return new Date(b.meetingStartAt).getTime() - new Date(b.bookedAt).getTime() > FAR_FUTURE_DAYS * 86_400_000;
}

const BOOKINGS_INFO = (
  <>
    Every demo booked on a Sequesto Calendly link, newest booking first. A demo is a Calendly event type named
    &ldquo;Demo&rdquo; (not a partner/co-sell slot); every other meeting is listed apart and never counted as a demo.
    The source is the Fillout form behind the booking (website demo and pricing forms, or an SDR personal form),
    matched on the Calendly event or on the same email booked within 30 minutes of the submission. An SDR form filled in
    for the same email from a day before to 14 days after the booking also makes it an SDR meeting, and so does a meeting
    Walter confirmed an SDR booked without a form (&ldquo;SDR (confirmed, no form)&rdquo;). With no form behind it, it
    falls back to the Calendly link, unless the person (in the 12 months before) or a colleague at the same company
    domain (in the 90 days before) had filled in a website form or a LinkedIn lead form: then it is
    &ldquo;Marketing-influenced (booked direct)&rdquo;, the Marketing tab&rsquo;s rule. Otherwise: &ldquo;Nadia&rsquo;s own page&rdquo; and &ldquo;Andrew&rsquo;s own page&rdquo; are
    their personal Calendly pages, &ldquo;Patrick&rsquo;s own page&rdquo; is calendly.com/sequesto. Test addresses are left out, a rescheduled meeting counts once, and the same
    person booking again within 30 days counts once (marked &ldquo;Repeat&rdquo;). A meeting set more than{' '}
    {FAR_FUTURE_DAYS} days after it was booked is flagged &ldquo;Far future&rdquo;: check the date with the
    person. The company is the Attio company with the invitee&rsquo;s email domain, matched the same way as on
    Prospect meetings.
  </>
);

/** Meetings → demo bookings: every demo booked on a Calendly link and where it came from. */
export function DemoBookings({
  leading,
  period,
  periodControl,
}: {
  leading?: ReactNode;
  period: MeetingsPeriod;
  periodControl?: ReactNode;
}) {
  const [source, setSource] = useState<DemoBookingSource | 'all'>('all');
  const [rep, setRep] = useState<SalesRepSlug | 'all'>('all');
  const booked = `Booked ${period.label}`;

  const { data, error, refetch } = useDemoBookings({ from: period.from, to: period.to });

  const visible = useMemo(() => {
    let rows = data?.bookings ?? [];
    if (source !== 'all') rows = rows.filter((b) => b.source === source);
    if (rep !== 'all') rows = rows.filter((b) => b.hostRepSlug === rep);
    return rows;
  }, [data, source, rep]);

  /** Counted demos per host rep (repeats left out, as in the headline). */
  const repCounts = useMemo(() => {
    const counts = new Map<SalesRepSlug, number>();
    for (const b of data?.bookings ?? []) {
      if (b.hostRepSlug && b.repeatOf === null) counts.set(b.hostRepSlug, (counts.get(b.hostRepSlug) ?? 0) + 1);
    }
    return counts;
  }, [data]);

  const sourceCounts = useMemo(() => {
    const byId = new Map(data?.countsBySource.map((c) => [c.source, c]) ?? []);
    return SOURCE_ORDER.map((id) => byId.get(id)).filter((c) => c !== undefined);
  }, [data]);

  const notes = data ? dataNotes(data) : [];
  const excluded = data ? excludedNote(data.excluded) : null;

  return (
    <div className="space-y-4">
      <SectionToolbar
        leading={leading}
        meta={
          data && (
            <>
              {data.sync.lastSuccessAt ? `From Calendly · synced ${formatDayMonthTime(data.sync.lastSuccessAt)}` : 'No successful Calendly sync yet'}
              <DataNotes notes={notes} />
            </>
          )
        }
        info={
          <>
            {BOOKINGS_INFO}
            {excluded && <> {excluded}</>}
          </>
        }
        actions={periodControl}
      />

      {error ? (
        <ErrorState title="Couldn’t load demo bookings" error={error} onRetry={() => refetch()} />
      ) : !data ? (
        <LoadingState rows={5} />
      ) : (
        <>
          {data.sync.problem && (
            <ErrorState
              compact
              title={
                data.sync.problem.kind === 'partial'
                  ? `The last Calendly sync (${formatDayMonthTime(data.sync.problem.at)}) only partly worked`
                  : `The last Calendly sync (${formatDayMonthTime(data.sync.problem.at)}) failed`
              }
              error={
                data.sync.lastSuccessAt
                  ? `Last good sync ${formatDayMonthTime(data.sync.lastSuccessAt)}. Bookings since then may be missing or out of date.`
                  : 'No fully successful sync on record.'
              }
              details={data.sync.problem.error}
            />
          )}
          {data.bookings.length === 0 && data.otherMeetings.length === 0 ? (
            <EmptyState
              title="No bookings in this window"
              description="The Calendly mirror runs nightly at 03:30. Run the “Demo Bookings Sync” automation to refresh it now."
            />
          ) : (
            <>
              <div className="space-y-2">
                {/* The booking window is said once here, not on each of the four Calendly tiles. */}
                <p className="text-body-xs text-text-tertiary">{booked} · Calendly</p>
                <div className={clsx('grid grid-cols-2 gap-4', data.sdrMeetings ? 'lg:grid-cols-5' : 'lg:grid-cols-4')}>
                  <StatTile
                    label="Demos"
                    value={data.demos.distinct}
                    sub={`${plural(data.demos.repeats, 'repeat')} counted once`}
                    tooltip={`Distinct demos booked on Calendly ${period.label} (${plural(data.demos.bookings, 'booking')}); the same person booking again within ${data.demos.repeatWindowDays} days counts once.`}
                  />
                  <StatTile
                    label="Upcoming"
                    value={data.demos.upcoming}
                    sub="Still to come"
                  />
                  <StatTile label="Cancelled" value={data.demos.cancelled} sub="Not rescheduled" />
                  <StatTile label="Other meetings" value={data.otherMeetings.length} sub="Not demos" />
                  {data.sdrMeetings && (
                    <StatTile
                      label="SDR meetings"
                      value={data.sdrMeetings.count}
                      sub={`Submitted ${period.label} · Fillout personal forms`}
                      tooltip={`${data.sdrMeetings.definition} Same count as the SDR tab.`}
                    />
                  )}
                </div>
              </div>
              {data.sdrMeetings && <SdrBridge bridge={data.sdrMeetings} />}

              <Card padding="md">
                <CardHeader
                  // Demos first (the tile's number), then the rows the table lists, repeats included.
                  title={demosTitle(visible)}
                  description={[demoEventLabel(visible), farFutureLine(visible)].filter(Boolean).join(' · ')}
                  action={
                    <div className="flex flex-wrap items-center gap-2">
                      <Select
                        size="sm"
                        value={source}
                        onChange={(e) => setSource(e.target.value as DemoBookingSource | 'all')}
                        aria-label="Source"
                      >
                        <option value="all">All sources · {data.demos.distinct}</option>
                        {sourceCounts.map((c) => (
                          <option key={c.source} value={c.source}>
                            {sourceName(c.source, c.label)} · {c.count}
                          </option>
                        ))}
                      </Select>
                      <Select
                        size="sm"
                        value={rep}
                        onChange={(e) => setRep(e.target.value as SalesRepSlug | 'all')}
                        aria-label="Host"
                      >
                        <option value="all">All hosts</option>
                        {[...repCounts.entries()].map(([slug, count]) => (
                          <option key={slug} value={slug}>
                            {getRep(slug).name} · {count}
                          </option>
                        ))}
                      </Select>
                    </div>
                  }
                />
                {visible.length === 0 ? (
                  <EmptyState title="No demo bookings match these filters" compact />
                ) : (
                  <BookingsTable rows={visible} showMeeting={demoEventLabel(visible) === undefined} />
                )}
              </Card>

              <OtherMeetings data={data} />
            </>
          )}
        </>
      )}
    </div>
  );
}

/** "57 demos (62 bookings incl. 5 repeats)", or "57 demos" when no row is a repeat. */
function demosTitle(rows: DemoBooking[]): string {
  const repeats = rows.filter((b) => b.repeatOf !== null).length;
  const demos = plural(rows.length - repeats, 'demo');
  return repeats > 0 ? `${demos} (${rows.length} bookings incl. ${plural(repeats, 'repeat')})` : demos;
}

/** "1 meeting set far in the future (19 Mar 2027)", or nothing. */
function farFutureLine(rows: DemoBooking[]): string | undefined {
  const far = rows.filter(isFarFuture);
  if (far.length === 0) return undefined;
  const dates = far.map((b) => formatShortDate(b.meetingStartAt)).join(', ');
  return `${plural(far.length, 'meeting')} set more than ${FAR_FUTURE_DAYS} days after booking (${dates})`;
}

/** The demo event type, said once in the header instead of on every row. */
function demoEventLabel(rows: DemoBooking[]): string | undefined {
  const names = new Set(rows.map((b) => b.eventTypeName || b.eventName).filter(Boolean));
  return names.size === 1 ? `Calendly event: ${[...names][0]}` : undefined;
}

/** Warnings about the numbers (amber pill in the meta line). Benign exclusions go in the grey (i) instead. */
function dataNotes(data: DemoBookingsResponse): ReactNode[] {
  const notes: ReactNode[] = [];
  if (!data.rangeComplete) {
    notes.push(
      `This window starts before ${data.completeFrom ? formatDayMonthYear(data.completeFrom) : 'the first sync'}, the earliest date the Calendly mirror fully covers: earlier bookings are missing, so the counts are too low.`,
    );
  }
  if ([...data.bookings, ...data.otherMeetings].some((b) => b.source === 'unclassified')) {
    notes.push(
      'Some bookings came in on a link the source rules don’t recognise. They sit under “Unclassified” with the raw link on hover. Say what each link is and the rule gets added.',
    );
  }
  // Test bookings and reschedule copies left out are expected, not a warning: they sit in the grey (i).
  return notes;
}

function excludedNote(excluded: DemoBookingsResponse['excluded']): string | null {
  const parts: string[] = [];
  if (excluded.tests > 0) parts.push(plural(excluded.tests, 'test booking'));
  if (excluded.rescheduledCopies > 0) parts.push(plural(excluded.rescheduledCopies, 'rescheduled-away copy', 'rescheduled-away copies'));
  if (excluded.rescheduleTargetMissing > 0) {
    parts.push(`${plural(excluded.rescheduleTargetMissing, 'rescheduled booking')} whose new slot is not synced`);
  }
  return parts.length > 0 ? `Left out: ${parts.join(', ')}.` : null;
}

/**
 * Demo bookings and the other meetings share this table. The Meeting column is
 * dropped when every row is the same event type (named in the card header).
 */
function BookingsTable({ rows, showMeeting = true }: { rows: DemoBooking[]; showMeeting?: boolean }) {
  const { pageItems, ...paging } = usePagination(rows);
  // One source on every row (an Other meetings list, or a filtered table) is said once above, not per row.
  const sourceOnce = isConstant(rows, (r) => `${r.source}:${sourceName(r.source, r.sourceLabel, r.hostRepSlug)}`);
  // A status pill is the exception; when no row has one the column would be empty.
  const showStatus = rows.some((r) => r.status === 'canceled' || r.rescheduled || isFarFuture(r) || r.repeatOf);
  const first = rows[0];
  return (
    <>
      {sourceOnce && first && (
        <p className="mb-2 text-body-xs text-text-tertiary">Source on every row: {sourceName(first.source, first.sourceLabel, first.hostRepSlug)}</p>
      )}
      {/* Fixed widths, so the columns don't move between pages of the table. */}
      <Table
        minWidth={760 + (sourceOnce ? 0 : 168) + (showMeeting ? 144 : 0) + (showStatus ? 104 : 0)}
        colWidths={[
          '7rem',
          ...(sourceOnce ? [] : ['10.5rem']),
          'auto',
          '11rem',
          '3.5rem',
          ...(showMeeting ? ['9rem'] : []),
          '8.5rem',
          ...(showStatus ? ['6.5rem'] : []),
          '2.5rem',
        ]}
      >
        <THead>
          <tr>
            <Th>Booked</Th>
            {!sourceOnce && <Th>Source</Th>}
            <Th>Who</Th>
            <Th>Company</Th>
            <Th align="center">Host</Th>
            {showMeeting && <Th>Meeting</Th>}
            <Th>Meeting on</Th>
            {showStatus && <Th>Status</Th>}
            <Th className="w-10 !pr-0">
              <span className="sr-only">Attio</span>
            </Th>
          </tr>
        </THead>
        <tbody>
          {pageItems.map((b) => <BookingRow key={b.id} booking={b} showMeeting={showMeeting} showSource={!sourceOnce} showStatus={showStatus} />)}
        </tbody>
      </Table>
      <Pagination {...paging} onPage={paging.setPage} />
    </>
  );
}

function OtherMeetings({ data }: { data: DemoBookingsResponse }) {
  const [open, setOpen] = useState(false);
  if (data.otherMeetings.length === 0) return null;
  const summary = data.otherMeetingsByKind.map((k) => `${k.count} ${KIND_LABELS[k.kind].toLowerCase()}`).join(', ');
  return (
    <Card padding="md">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-1.5 text-left text-body-sm font-medium text-text-secondary hover:text-text-primary"
      >
        <ChevronRight className={clsx('h-4 w-4 transition-transform', open && 'rotate-90')} />
        Other meetings
        <span className="font-normal text-text-tertiary">
          {data.otherMeetings.length} booked in the period{summary ? `: ${summary}` : ''}
        </span>
      </button>
      {open && (
        <div className="mt-3">
          <BookingsTable rows={data.otherMeetings} />
        </div>
      )}
    </Card>
  );
}

function BookingRow({ booking, showMeeting, showSource, showStatus }: { booking: DemoBooking; showMeeting: boolean; showSource: boolean; showStatus: boolean }) {
  const canceled = booking.status === 'canceled';
  const company = bookingCompany(booking);
  return (
    <Tr className={clsx(canceled && 'opacity-60')}>
      <Td className="whitespace-nowrap tabular-nums text-text-secondary">{formatShortDateTime(booking.bookedAt)}</Td>
      {showSource && (
        <Td className="pr-4">
          <span title={[SOURCE_HINTS[booking.source], booking.sourceDetail].filter(Boolean).join(' · ') || undefined}>
            <Badge variant={SOURCE_VARIANT[booking.source]}>
              {sourceName(booking.source, booking.sourceLabel, booking.hostRepSlug)}
            </Badge>
          </span>
        </Td>
      )}
      <Td>
        <div className={clsx('break-words text-text-primary', canceled && 'line-through')}>
          {booking.inviteeName || booking.inviteeEmail}
        </div>
        {booking.inviteeName && <div className="break-all text-body-xs text-text-tertiary">{booking.inviteeEmail}</div>}
      </Td>
      <Td className="text-text-secondary">
        {/* The Attio name first; the email domain as the grey line when Attio names it differently. */}
        {company.attioName ? (
          <>
            <div title={company.attioName}>{company.attioName}</div>
            {company.typed && companyLabel(company.typed) !== company.attioName && (
              <div className="text-body-xs text-text-tertiary" title={company.typed}>{company.typed}</div>
            )}
          </>
        ) : company.typed ? (
          <>
            <div title={companyLabel(company.typed)}>{companyLabel(company.typed)}</div>
            <div
              className="text-body-xs text-text-tertiary"
              title={company.webmail ? 'Typed in the booking form; a personal email address is never matched to a company' : `No Attio company has the domain ${company.typed}`}
            >
              {company.webmail ? 'from the form' : 'not in Attio'}
            </div>
          </>
        ) : company.webmail ? (
          <span className="text-body-xs text-text-tertiary" title="A personal email address (webmail) says nothing about the company, so none is shown">
            personal email
          </span>
        ) : (
          <Nil />
        )}
      </Td>
      <Td align="center">
        {booking.hostRepSlug ? (
          <RepInitials rep={getRep(booking.hostRepSlug)} />
        ) : booking.hostEmails[0] ? (
          <span className="text-body-xs text-text-tertiary">{booking.hostEmails[0]}</span>
        ) : (
          <Nil />
        )}
      </Td>
      {showMeeting && (
        <Td>
          <div title={booking.schedulingUrl ?? undefined} className="text-text-primary">
            {booking.eventTypeName || booking.eventName || <Nil />}
          </div>
          {!booking.isDemo && booking.meetingKind !== 'other' && <div className="text-body-xs text-text-tertiary">{KIND_LABELS[booking.meetingKind]}</div>}
        </Td>
      )}
      <Td className="whitespace-nowrap tabular-nums text-text-secondary">{formatShortDateTime(booking.meetingStartAt)}</Td>
      {showStatus && (
      <Td>
        {/* Only the exceptions get a pill; a live booking is the default and says nothing. */}
        <div className="flex flex-wrap gap-1">
          {canceled && <Badge variant="error">Cancelled</Badge>}
          {booking.rescheduled && <Badge>Rescheduled</Badge>}
          {isFarFuture(booking) && (
            <span title={`Booked ${formatShortDate(booking.bookedAt)} for ${formatShortDate(booking.meetingStartAt)}: more than ${FAR_FUTURE_DAYS} days ahead. Check the date with the person.`}>
              <Badge variant="warning">Far future</Badge>
            </span>
          )}
          {booking.repeatOf && (
            <span title="Same person booked a demo in the previous 30 days, so it is counted once">
              <Badge variant="warning">Repeat</Badge>
            </span>
          )}
        </div>
      </Td>
      )}
      <Td align="right" className="!pr-0">
        <AttioLink
          href={company.attioName && booking.attioCompanyRecordId ? ATTIO_COMPANY_URL(booking.attioCompanyRecordId) : null}
          label={company.attioName ?? 'company'}
        />
      </Td>
    </Tr>
  );
}
